/**
 * Finish upcharge math.
 *
 * Every surface that shows or charges a finish upcharge — backend SSR enrichment, PDP price block,
 * add-to-cart payload, cart line, order — must arrive at the identical cent value. The clamping and
 * rounding below are the reason that holds, so keep all of them going through this helper rather
 * than multiplying inline.
 *
 * This lives in the shared `types` package (imported as `@Types/utils/finishUpcharge` from the
 * backend and `shared/types/utils/finishUpcharge` from the frontend) precisely so the server-rendered
 * price and the post-hydration price cannot drift.
 */

/**
 * Finishes that cost nothing by default: bare wood, bare metal, and the whole standard
 * powder-coat tier. Only premium tiers upcharge unless an explicit multiplier says otherwise.
 */
const FREE_BASELINE_FINISHES = new Set([
  'unfinished',
  'brushed stainless',
  'standard black',
  'standard silver',
  'standard white',
]);

/** Multipliers are authored per finish; clamp so a bad value can't invent a huge surcharge. */
const MAX_MULTIPLIER = 2;

export const finishMultiplier = (finishName: string | undefined, multiplier: number | undefined) => {
  if (typeof multiplier === 'number' && Number.isFinite(multiplier)) {
    return Math.min(Math.max(multiplier, 0), MAX_MULTIPLIER);
  }
  // No authored multiplier — fall back to the tier the name implies.
  return FREE_BASELINE_FINISHES.has((finishName ?? '').trim().toLowerCase()) ? 0 : 1;
};

/**
 * The finish upcharge in whole cents.
 *
 * Rounds twice deliberately: once after the multiplier, then again after the sale factor.
 * Rounding only at the end lets a fractional cent survive into the total, where it drifts a
 * cent apart from whatever the cart computed and the shopper sees a price we don't charge.
 *
 * @param saleFactor ratio of discounted to list price; pass 1 (the default) for the list-price
 * upcharge, which is what a struck-through "was" price should add.
 */
export const finishUpchargeCents = (
  finishPriceCents: number | undefined,
  finishName: string | undefined,
  multiplier: number | undefined,
  saleFactor = 1,
) => {
  if (!finishPriceCents || !Number.isFinite(finishPriceCents)) return 0;

  const listUpcharge = Math.round(finishPriceCents * finishMultiplier(finishName, multiplier));
  return Math.round(listUpcharge * saleFactor);
};

/** Ratio of what a variant actually sells for to its list price; 1 when it is not discounted. */
export const getSaleFactor = (basePriceCents?: number, listPriceCents?: number) => {
  if (!listPriceCents || listPriceCents <= 0 || typeof basePriceCents !== 'number') return 1;
  return basePriceCents / listPriceCents;
};
